import { apiClient } from './apiClient';
import type { DataMode } from '@/contexts/DataSourceContext';
import { DEFAULT_ROLE_PERMISSIONS_MAP } from './defaultRolePermissions';

// Types for UserRole API
export interface UserRoleListItem {
  id: number;
  name: string;
  description?: string;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  createdAt: string;
}

// UserRolePermissions aligned with PERMISSIONS_LIST.md (62 permissions in 9 categories)
// The 'permissions' array stores permission codes like "dashboard.view_own", "timesheet.create", etc.
// The nested objects provide detailed settings beyond basic permission codes
export interface UserRolePermissions {
  permissions?: string[];
  timesheetAccess?: {
    viewAccess: 'none' | 'own' | 'team' | 'all';
    editAccess: 'none' | 'own' | 'team' | 'all';
    allowDelete: boolean;
    allowDuplicate: boolean;
    allowRecall: boolean;
  };
  smartFeaturesPermissions?: {
    enableCopyFromLastWeek: boolean;
    enableCopyDescription: boolean;
    enableCopyOnlyBillable: boolean;
    enableAutoSmartFill: boolean;
    enableAiInsights: boolean;
  };
  approvalPermissions?: {
    viewDashboard: boolean;
  };
  timesheetApprovalPermissions?: {
    viewApprovals: boolean;
    editTimesheets: boolean;
    rejectTimesheets: boolean;
    compulsoryRejectionReason: boolean;
    bulkApprove: boolean;
    delegateApproval: boolean;
    viewPolicyViolations: boolean;
    viewDetectedIssues: boolean;
  };
  reportAccessPermissions?: {
    timesheetReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    missingTimesheetReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    utilisationReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    // billableHoursReport removed — belongs in the Projects module of the PSA suite
  };
  // Team Management permissions (6 permissions from PERMISSIONS_LIST.md)
  teamManagementPermissions?: {
    viewDirectory: boolean;
    viewDetails: boolean;

  };
  // Project Management permissions (8 permissions from PERMISSIONS_LIST.md)
  projectManagementPermissions?: {
    viewAssigned: boolean;
    viewAll: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    archive: boolean;
    assign: boolean;
    manageBudget: boolean;
  };
  employeeDirectoryPermissions?: {
    viewDirectory: boolean;
    viewDetails: boolean;
  };
  integrationsPermissions?: {
    viewIntegrations: boolean;
    manageIntegrations: boolean;
  };
  companyManagementPermissions?: {
    viewCompanies: boolean;
    manageCompanies: boolean;
  };
  systemSettingsPermissions?: {
    manageGeneralSettings: 'view' | 'configure';
    manageDeadlines: boolean;
    managePolicySettings: boolean;
    manageUtilisationSettings: boolean;
    manageTimeEntrySettings?: boolean;   // ⚠️ new — requires backend field
    manageWorkTypeSettings?: boolean;    // ⚠️ new — requires backend field
    manageApprovalsSettings: boolean;
    manageNotifications: boolean;
    manageRolesPermissions: boolean;
    manageScreenDataModes: boolean;
  };
  // Data Operations permissions (3 permissions from PERMISSIONS_LIST.md)
  dataOperationsPermissions?: {
    import: boolean;
    export: boolean;
    bulkOperations: boolean;
  };
}

export interface UserRoleEmployee {
  id: number;
  name: string;
  email: string;
  roleName?: string;
  status: string;
  assignedAt: string;
}

export interface UserRole {
  id: number;
  organisationId: string;
  name: string;
  description?: string;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  createdAt: string;
  createdBy: number;
  createdByName?: string;
  updatedAt?: string;
  updatedBy?: number;
  updatedByName?: string;
  permissions?: UserRolePermissions;
  users?: UserRoleEmployee[];
}

export interface CreateUserRoleDto {
  name: string;
  description?: string;
  isActive?: boolean;
  permissions?: UserRolePermissions;
}

export interface UpdateUserRoleDto {
  name: string;
  description?: string;
  isActive?: boolean;
  permissions?: UserRolePermissions;
}

// Real API service
const realUserRoleService = {
  async getAllUserRoles(): Promise<UserRoleListItem[]> {
    return await apiClient.get<UserRoleListItem[]>('/userroles');
  },

  async getUserRoleById(id: number): Promise<UserRole> {
    return await apiClient.get<UserRole>(`/userroles/${id}`);
  },

  async createUserRole(dto: CreateUserRoleDto): Promise<UserRole> {
    return await apiClient.post<UserRole>('/userroles', dto);
  },

  async updateUserRole(id: number, dto: UpdateUserRoleDto): Promise<UserRole> {
    return await apiClient.put<UserRole>(`/userroles/${id}`, dto);
  },

  async deleteUserRole(id: number): Promise<void> {
    await apiClient.delete(`/userroles/${id}`);
  },

  async getEmployeesByRoleId(roleId: number): Promise<UserRoleEmployee[]> {
    return await apiClient.get<UserRoleEmployee[]>(`/userroles/${roleId}/employees`);
  },

  async assignEmployeeToRole(roleId: number, employeeId: number): Promise<void> {
    await apiClient.post(`/userroles/${roleId}/employees/${employeeId}`, {});
  },

  async removeEmployeeFromRole(roleId: number, employeeId: number): Promise<void> {
    await apiClient.delete(`/userroles/${roleId}/employees/${employeeId}`);
  },
};

// Mock data for demo mode
const defaultMockUserRoles: UserRoleListItem[] = [
  {
    id: 1,
    name: 'Administrator',
    description: 'Full system access and control',
    userCount: 3,
    isSystem: true,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 2,
    name: 'User',
    description: 'Standard user access for time entry and basic features',
    userCount: 25,
    isSystem: true,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 3,
    name: 'Approver',
    description: 'Can approve timesheets and view team reports',
    userCount: 8,
    isSystem: true,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 4,
    name: 'Finance Manager',
    description: 'Access to financial reports and billing information',
    userCount: 2,
    isSystem: false,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
];

const STORAGE_KEY = 'userRoles';

const getStoredRoles = (): UserRoleListItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading user roles from localStorage:', error);
  }
  return defaultMockUserRoles;
};

const saveRoles = (roles: UserRoleListItem[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
  } catch (error) {
    console.error('Error saving user roles to localStorage:', error);
  }
};

// Mock service for demo mode
const mockUserRoleService = {
  async getAllUserRoles(): Promise<UserRoleListItem[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return getStoredRoles();
  },

  async getUserRoleById(id: number): Promise<UserRole> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const roles = getStoredRoles();
    const role = roles.find(r => r.id === id);
    if (!role) {
      throw new Error(`UserRole with ID ${id} not found`);
    }
    
    // Load permissions from localStorage — fall back to default template for
    // system roles (ids 1, 2, 3) so new organisations start with sensible defaults
    const permissionsKey = `userRolePermissions_${id}`;
    let permissions: UserRolePermissions = DEFAULT_ROLE_PERMISSIONS_MAP[id] ?? {};
    try {
      const storedPermissions = localStorage.getItem(permissionsKey);
      if (storedPermissions) {
        permissions = JSON.parse(storedPermissions);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }

    return {
      ...role,
      organisationId: 'mock-org-id',
      createdBy: 1,
      permissions,
      users: [],
    };
  },

  async createUserRole(dto: CreateUserRoleDto): Promise<UserRole> {
    await new Promise(resolve => setTimeout(resolve, 500));
    const roles = getStoredRoles();
    const newId = Math.max(...roles.map(r => r.id), 0) + 1;
    
    const newRole: UserRoleListItem = {
      id: newId,
      name: dto.name,
      description: dto.description,
      isSystem: false,
      isActive: dto.isActive ?? true,
      userCount: 0,
      createdAt: new Date().toISOString(),
    };
    
    roles.push(newRole);
    saveRoles(roles);

    // Save permissions
    if (dto.permissions) {
      const permissionsKey = `userRolePermissions_${newId}`;
      localStorage.setItem(permissionsKey, JSON.stringify(dto.permissions));
    }

    return {
      ...newRole,
      organisationId: 'mock-org-id',
      createdBy: 1,
      permissions: dto.permissions,
      users: [],
    };
  },

  async updateUserRole(id: number, dto: UpdateUserRoleDto): Promise<UserRole> {
    await new Promise(resolve => setTimeout(resolve, 500));
    const roles = getStoredRoles();
    const index = roles.findIndex(r => r.id === id);
    
    if (index === -1) {
      throw new Error(`UserRole with ID ${id} not found`);
    }

    const existingRole = roles[index];
    const updatedRole: UserRoleListItem = {
      ...existingRole,
      name: dto.name,
      description: dto.description,
      isActive: existingRole.isSystem ? true : (dto.isActive ?? existingRole.isActive),
    };

    roles[index] = updatedRole;
    saveRoles(roles);

    // Save permissions
    if (dto.permissions) {
      const permissionsKey = `userRolePermissions_${id}`;
      localStorage.setItem(permissionsKey, JSON.stringify(dto.permissions));
    }

    return {
      ...updatedRole,
      organisationId: 'mock-org-id',
      createdBy: 1,
      permissions: dto.permissions,
      users: [],
    };
  },

  async deleteUserRole(id: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const roles = getStoredRoles();
    const role = roles.find(r => r.id === id);
    
    if (!role) {
      throw new Error(`UserRole with ID ${id} not found`);
    }
    
    if (role.isSystem) {
      throw new Error('System roles cannot be deleted');
    }

    const filteredRoles = roles.filter(r => r.id !== id);
    saveRoles(filteredRoles);

    // Remove permissions
    const permissionsKey = `userRolePermissions_${id}`;
    localStorage.removeItem(permissionsKey);
  },

  async getEmployeesByRoleId(_roleId: number): Promise<UserRoleEmployee[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    // Return mock users
    return [
      { id: 1, name: 'John Smith', email: 'john.smith@example.com', roleName: 'Developer', status: 'active', assignedAt: new Date().toISOString() },
      { id: 2, name: 'Sarah Johnson', email: 'sarah.johnson@example.com', roleName: 'Designer', status: 'active', assignedAt: new Date().toISOString() },
    ];
  },

  async assignEmployeeToRole(_roleId: number, _employeeId: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    // Mock implementation - in real app would update database
  },

  async removeEmployeeFromRole(_roleId: number, _employeeId: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    // Mock implementation - in real app would update database
  },
};

// Get the appropriate service based on the data mode
export const getUserRoleService = (mode: DataMode) => {
  return mode === 'live' ? realUserRoleService : mockUserRoleService;
};

// Default export for backward compatibility (uses mock by default)
export const userRoleService = mockUserRoleService;
